import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { updateDocInDb } from "../../../../../app/firestore/firestoreService";

const PREFIX = "CALCULATE ";

function isCalculateField(name = "") {
  return name.startsWith(PREFIX);
}

function toNumber(v) {
  if (v == null) return 0;
  const cleaned = String(v).replace(/[$,]/g, "").trim();
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

function tokenizeFormula(formulaText) {
  const regex = /\s+(PLUS|MINUS|TIMES|DIVIDE)\s+/g;
  return formulaText
    .split(regex)
    .map((s) => s.trim())
    .filter(Boolean);
}

function evalTokens(tokens, valuesByName) {
  if (!tokens.length) return 0;
  let acc = toNumber(valuesByName[tokens[0]]);

  for (let i = 1; i < tokens.length; i += 2) {
    const op = tokens[i];
    const termName = tokens[i + 1];
    const val = toNumber(valuesByName[termName]);

    switch (op) {
      case "PLUS":
        acc += val;
        break;
      case "MINUS":
        acc -= val;
        break;
      case "TIMES":
        acc *= val;
        break;
      case "DIVIDE":
        acc = val === 0 ? 0 : acc / val;
        break;
      default:
        break;
    }
  }
  return acc;
}

export function FormFieldText({
  formField,
  index,
  populatingFieldsStatus,
  newPopulatedFields,
}) {
  const { pageScaleFill } = useSelector((state) => state.annot);
  const { doc } = useSelector((state) => state.doc);

  const pageScale = pageScaleFill;
  const formFieldValues = doc.formFieldValues || {};

  const calc = isCalculateField(formField.name);
  const formulaText = calc ? formField.name.slice(PREFIX.length).trim() : "";


  const tokens = useMemo(
    () => (calc ? tokenizeFormula(formulaText) : []),
    [calc, formulaText]
  );

  const depFieldNames = useMemo(() => {
    if (!calc) return [];
    return tokens.filter((_, idx) => idx % 2 === 0); // terms
  }, [calc, tokens]);

  const depSignature = useMemo(() => {
  if (!calc) return "";
  return depFieldNames
    .map((name) => String(formFieldValues?.[name] ?? ""))
    .join("|");
}, [calc, depFieldNames, formFieldValues]);

  // Override flag stored in Firestore under doc.formFieldCalcOverrides
  const isOverridden = !!doc.formFieldCalcOverrides?.[formField.name];

  const [value, setValue] = useState(formFieldValues?.[formField.name] || "");
  const lastAutoSavedRef = useRef(null);

  // Sync local state when doc value changes externally
  useEffect(() => {
    setValue(formFieldValues?.[formField.name] ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc.id, formField.name, formFieldValues?.[formField.name]]);

  // Prepopulate rerender catch (your existing logic)
  useEffect(() => {
    if (
      populatingFieldsStatus === "Complete" &&
      newPopulatedFields?.[formField.name]
    ) {
      setValue(newPopulatedFields[formField.name]);
    }
  }, [populatingFieldsStatus, formField.name, newPopulatedFields]);

  // Auto-calc ONLY when not overridden
  useEffect(() => {
    if (!calc) return;
    if (isOverridden) return;

    const computed = evalTokens(tokens, formFieldValues);

    // Format choice: keep as plain number string; swap to toFixed(2) if desired
    const nextValue = Number.isFinite(computed) ? String(computed) : "";

    if (String(value ?? "") !== nextValue) {
      setValue(nextValue);
    }

    // Avoid spamming identical saves
    if (lastAutoSavedRef.current !== nextValue) {
      lastAutoSavedRef.current = nextValue;
      updateDocInDb(doc.id, {
        [`formFieldValues.${formField.name}`]: nextValue,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    calc,
    isOverridden,
    doc.id,
    formField.name,
    tokens,
    depSignature,
  ]);

  function handleChange(e) {
    const newVal = e.target.value;
    setValue(newVal);

    // If user edits a CALCULATE field, mark it overridden (but don’t write value yet)
    if (calc && !isOverridden) {
      updateDocInDb(doc.id, {
        [`formFieldCalcOverrides.${formField.name}`]: true,
      });
    }
  }

  function handleBlur(e) {
    let newValue = e.target.value;
    newValue = newValue.replace(/[\u{0080}-\u{FFFF}]/gu, "");

    // If user clears a CALCULATE field, revert to auto mode
    if (calc && newValue.trim() === "") {
      updateDocInDb(doc.id, {
        [`formFieldCalcOverrides.${formField.name}`]: false,
      });
      // Don’t save empty; the auto effect will recompute next tick
      return;
    }

    // Save value normally (works for both normal fields and overridden calc fields)
    updateDocInDb(doc.id, {
      [`formFieldValues.${formField.name}`]: newValue,
    });
  }

  // const textColor =
  // calc && isOverridden
  //   ? "#0000CC"   // blue when overridden
  //   : "black";    // black otherwise
    
  return (
    <div
      className="pdfFormField"
      style={{
        top: `${formField.top * pageScale}px`,
        left: `${formField.left * pageScale}px`,
        height: `${formField.height * pageScale}px`,
        width: `${formField.width * pageScale}px`,
        fontSize: `${formField.fontSize * pageScale}px`,
      }}
    >
      <input
        className="fill-input-field"
        type={formField.type}
        name={formField.name}
        aria-label={formField.name}
        tabIndex={index}
        data-tabindex={index}
        autoComplete="off"
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        style={{
          borderWidth: "0px",
          fontFamily: "Helvetica",
          fontSize: `${formField.fontSize * pageScale}px`,
          margin: "0px",
          textAlign: "left",
          color: "blue",
          ...(calc && !isOverridden ? {color: "black"} : {color: "blue"}),
          // Optional subtle cue when overridden
          // backgroundColor: calc && isOverridden ? "#fff7e6" : "transparent",
        }}
      />
    </div>
  );
}
